import React, { useCallback } from 'react'

interface UseFocusRingOptions {
  doClose: () => void
  listRef: React.RefObject<HTMLElement>
}

export default function useFocusRing({ doClose, listRef }: UseFocusRingOptions) {
  const onBlur = useCallback<React.FocusEventHandler>(
    (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        doClose()
      }
    },
    [doClose]
  )

  const onKeyDown = useCallback<React.KeyboardEventHandler>(
    (e) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
        e.preventDefault()

        if (!listRef.current) {
          return
        }

        const current = listRef.current.querySelector(':focus')
        const elements = Array.from(listRef.current.querySelectorAll('a, input, button, select, [tabindex]'))

        if (!elements.length) {
          listRef.current.focus()
        } else {
          let idx = elements.findIndex((e) => e === current)

          if (e.key === 'ArrowUp') {
            idx = (idx - 1 + elements.length) % elements.length
          } else if (e.key === 'ArrowDown') {
            idx = (idx + 1) % elements.length
          } else if (['Home', 'PageUp'].includes(e.key)) {
            idx = 0
          } else if (['End', 'PageDown'].includes(e.key)) {
            idx = elements.length - 1
          }

          ;(elements[idx] as any).focus?.()
        }
      } else if (['Tab', 'Escape'].includes(e.key)) {
        e.preventDefault()
        doClose()
      }
    },
    [doClose, listRef]
  )

  const focus = useCallback(() => {
    if (!listRef.current) {
      return
    }
    const element = listRef.current.querySelector('a, input, button, select, [tabindex]') || listRef.current
    setTimeout(() => (element as any).focus?.(), 10)
  }, [listRef])

  return [{ onBlur, onKeyDown }, { focus }] as const
}
